import { isJsonRecord } from '../runtime-validation';
// =============================================================================
// Task Types
// =============================================================================
export const TASK_STATUSES = [
    'draft',
    'ready',
    'queued',
    'delegated',
    'in_progress',
    'sleeping',
    'completed',
    'failed',
    'cancelled',
];
export const TASK_TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];
/** Runtime type guard for TaskStatus values from untrusted sources (e.g. database rows). */
export function isTaskStatus(value) {
    return typeof value === 'string' && TASK_STATUSES.includes(value);
}
export const TASK_MODES = ['task', 'conversation'];
/** Runtime type guard for TaskMode values from untrusted sources (e.g. database rows). */
export function isTaskMode(value) {
    return typeof value === 'string' && TASK_MODES.includes(value);
}
/**
 * Tracks where the task runner is during async execution.
 * Persisted to the task record so stuck-task recovery knows WHERE execution stalled.
 */
export const TASK_EXECUTION_STEPS = [
    'node_selection',
    'waiting_for_node_capacity',
    'node_provisioning',
    'node_agent_ready',
    'workspace_creation',
    'workspace_dispatch',
    'workspace_ready',
    'attachment_transfer',
    'agent_session',
    'running',
    'awaiting_followup',
];
/**
 * Compile-time helper for execution-step write sites.
 *
 * Drizzle currently types the D1 `execution_step` column as `string | null`, so
 * a raw literal like `'agent_running'` can compile even though API mappers later
 * reject it. Route task writes through this helper when persisting a
 * TaskExecutionStep literal so TypeScript checks the shared contract at the
 * writer boundary.
 */
export function taskExecutionStep(step) {
    return step;
}
export function isTaskExecutionStep(value) {
    return typeof value === 'string' && TASK_EXECUTION_STEPS.includes(value);
}
/** Human-readable labels for each execution step (TDF-8). */
export const EXECUTION_STEP_LABELS = {
    node_selection: 'Finding a server...',
    waiting_for_node_capacity: 'Waiting for server capacity...',
    node_provisioning: 'Setting up a new server...',
    node_agent_ready: 'Waiting for server to start...',
    workspace_creation: 'Creating workspace...',
    workspace_dispatch: 'Starting workspace on server...',
    workspace_ready: 'Setting up development environment...',
    attachment_transfer: 'Uploading attachments to workspace...',
    agent_session: 'Starting AI agent...',
    running: 'Agent is working...',
    awaiting_followup: 'Waiting for follow-up...',
};
/** Ordered index for execution step progress — derived from TASK_EXECUTION_STEPS array position (TDF-8). */
export const EXECUTION_STEP_ORDER = Object.fromEntries(TASK_EXECUTION_STEPS.map((step, i) => [step, i]));
/**
 * Wake-specific wording for each execution step.
 *
 * A sleeping conversation is woken by a replacement TaskRunner, so its progress
 * is reported through the same `TASK_EXECUTION_STEPS` vocabulary as any other
 * task. The user-facing framing differs though: this is a session being restored,
 * not a task being launched, so `EXECUTION_STEP_LABELS` ("Uploading attachments",
 * "Waiting for follow-up") reads wrong here. Same steps, wake-shaped words.
 *
 * Keep this exhaustive over `TaskExecutionStep` — the `Record` type enforces it,
 * and `packages/shared/tests/unit/wake-phase-labels.test.ts` pins it.
 */
export const WAKE_PHASE_LABELS = {
    node_selection: 'Finding a server...',
    waiting_for_node_capacity: 'Waiting for server capacity...',
    node_provisioning: 'Provisioning a server...',
    node_agent_ready: 'Waiting for the server to start...',
    workspace_creation: 'Recreating your workspace...',
    workspace_dispatch: 'Starting your workspace...',
    workspace_ready: 'Restoring your session...',
    attachment_transfer: 'Restoring your files...',
    agent_session: 'Starting the agent...',
    running: 'Session restored.',
    awaiting_followup: 'Session restored.',
};
/** Fallback shown while a wake is claimed but no execution step has been reported yet. */
export const WAKE_PHASE_PENDING_LABEL = 'Waking and restoring session...';
/**
 * Human-readable wake progress text. Falls back to the generic pending label when
 * the phase is absent — a wake that has been claimed in D1 but whose replacement
 * TaskRunner has not yet written its first execution step.
 */
export function wakePhaseLabel(step) {
    return step ? WAKE_PHASE_LABELS[step] : WAKE_PHASE_PENDING_LABEL;
}
export const COMPLETION_EVIDENCE_VERIFICATION_KINDS = [
    'test',
    'staging',
    'manual',
    'ci',
    'other',
];
const COMPLETION_EVIDENCE_LIMITS = {
    maxTestsRun: 25,
    maxVerifications: 25,
    maxCommandLength: 500,
    maxDetailLength: 2000,
    maxDescriptionLength: 2000,
    maxEvidenceLength: 2000,
    maxPrUrlLength: 500,
    maxNotesLength: 4000,
};
function optionalTrimmedString(value, field, maxLength) {
    if (value === undefined)
        return { ok: true };
    if (typeof value !== 'string') {
        return { ok: false, error: `${field} must be a string` };
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return { ok: false, error: `${field} must not be empty` };
    }
    if (trimmed.length > maxLength) {
        return { ok: false, error: `${field} must be ${maxLength} characters or fewer` };
    }
    return { ok: true, value: trimmed };
}
function validateEvidenceArray(value, field, maxItems, parseItem) {
    if (!Array.isArray(value)) {
        return { ok: false, error: `${field} must be an array` };
    }
    if (value.length > maxItems) {
        return { ok: false, error: `${field} must contain ${maxItems} items or fewer` };
    }
    const parsed = [];
    for (const [index, item] of value.entries()) {
        if (!isJsonRecord(item)) {
            return { ok: false, error: `${field}[${index}] must be an object` };
        }
        const result = parseItem(item, index);
        if (!result.ok)
            return result;
        parsed.push(result.value);
    }
    return { ok: true, value: parsed };
}
function requiredEvidenceString(value, field, maxLength) {
    const result = optionalTrimmedString(value, field, maxLength);
    if (!result.ok)
        return result;
    if (!result.value) {
        return { ok: false, error: `${field} must not be empty` };
    }
    return { ok: true, value: result.value };
}
function validateCompletionTestRun(item, index) {
    const command = requiredEvidenceString(item.command, `evidence.testsRun[${index}].command`, COMPLETION_EVIDENCE_LIMITS.maxCommandLength);
    if (!command.ok)
        return command;
    if (typeof item.passed !== 'boolean') {
        return { ok: false, error: `evidence.testsRun[${index}].passed must be a boolean` };
    }
    const detail = optionalTrimmedString(item.detail, `evidence.testsRun[${index}].detail`, COMPLETION_EVIDENCE_LIMITS.maxDetailLength);
    if (!detail.ok)
        return detail;
    return {
        ok: true,
        value: {
            command: command.value,
            passed: item.passed,
            ...(detail.value ? { detail: detail.value } : {}),
        },
    };
}
function validateCompletionVerification(item, index) {
    if (typeof item.kind !== 'string' ||
        !COMPLETION_EVIDENCE_VERIFICATION_KINDS.includes(item.kind)) {
        return {
            ok: false,
            error: `evidence.verifications[${index}].kind must be one of: ${COMPLETION_EVIDENCE_VERIFICATION_KINDS.join(', ')}`,
        };
    }
    const description = requiredEvidenceString(item.description, `evidence.verifications[${index}].description`, COMPLETION_EVIDENCE_LIMITS.maxDescriptionLength);
    if (!description.ok)
        return description;
    const evidence = optionalTrimmedString(item.evidence, `evidence.verifications[${index}].evidence`, COMPLETION_EVIDENCE_LIMITS.maxEvidenceLength);
    if (!evidence.ok)
        return evidence;
    return {
        ok: true,
        value: {
            kind: item.kind,
            description: description.value,
            ...(evidence.value ? { evidence: evidence.value } : {}),
        },
    };
}
export function validateCompletionEvidence(value) {
    if (!isJsonRecord(value)) {
        return { ok: false, error: 'evidence must be an object' };
    }
    const output = {};
    let populatedFields = 0;
    if (value.testsRun !== undefined) {
        const testsRun = validateEvidenceArray(value.testsRun, 'evidence.testsRun', COMPLETION_EVIDENCE_LIMITS.maxTestsRun, validateCompletionTestRun);
        if (!testsRun.ok)
            return testsRun;
        output.testsRun = testsRun.value;
        populatedFields += 1;
    }
    if (value.verifications !== undefined) {
        const verifications = validateEvidenceArray(value.verifications, 'evidence.verifications', COMPLETION_EVIDENCE_LIMITS.maxVerifications, validateCompletionVerification);
        if (!verifications.ok)
            return verifications;
        output.verifications = verifications.value;
        populatedFields += 1;
    }
    const prUrl = optionalTrimmedString(value.prUrl, 'evidence.prUrl', COMPLETION_EVIDENCE_LIMITS.maxPrUrlLength);
    if (!prUrl.ok)
        return prUrl;
    if (prUrl.value) {
        output.prUrl = prUrl.value;
        populatedFields += 1;
    }
    const notes = optionalTrimmedString(value.notes, 'evidence.notes', COMPLETION_EVIDENCE_LIMITS.maxNotesLength);
    if (!notes.ok)
        return notes;
    if (notes.value) {
        output.notes = notes.value;
        populatedFields += 1;
    }
    if (populatedFields === 0) {
        return { ok: false, error: 'evidence must include at least one supported field' };
    }
    return { ok: true, value: output };
}
export function parseCompletionEvidenceJson(raw) {
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        const validation = validateCompletionEvidence(parsed);
        return validation.ok ? validation.value : null;
    }
    catch {
        return null;
    }
}
/** Default attachment size/count limits — all overridable via env vars. */
export const ATTACHMENT_DEFAULTS = {
    /** Maximum file size per attachment in bytes (default: 50MB). Env: ATTACHMENT_UPLOAD_MAX_BYTES */
    UPLOAD_MAX_BYTES: 50 * 1024 * 1024,
    /** Maximum total batch size in bytes (default: 200MB). Env: ATTACHMENT_UPLOAD_BATCH_MAX_BYTES */
    UPLOAD_BATCH_MAX_BYTES: 200 * 1024 * 1024,
    /** Maximum number of files per task (default: 20). Env: ATTACHMENT_MAX_FILES */
    MAX_FILES: 20,
    /** Presigned URL expiry in seconds (default: 900 = 15min). Env: ATTACHMENT_PRESIGN_EXPIRY_SECONDS */
    PRESIGN_EXPIRY_SECONDS: 900,
};
/** Regex for safe filenames — matches the VM agent pattern. */
export const SAFE_FILENAME_REGEX = /^[a-zA-Z0-9._\- ]+$/;
export const AGENT_ACTIVITY_STATES = ['working', 'awake-idle', 'sleeping', 'superseded'];
//# sourceMappingURL=task.js.map